// apps/web/src/lib/analytics.ts
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import posthog from 'posthog-js'
import {
  identifyUser,
  setAnalyticsSuppressed,
  setUserRole,
  isEnabled,
} from '@intelhub/analytics/client'
import { flattenPatterns, sanitizeUrl } from '@intelhub/analytics/sanitize'
import { REPLAY_POLICY } from '@intelhub/analytics/registry'
import { shouldRecord } from '@intelhub/analytics/replay-policy'
import type { UserRole } from '@intelhub/analytics/envelope'
import { useAuth } from '@/contexts/AuthContext'

const PATTERNS = flattenPatterns(REPLAY_POLICY.intelhub)

/**
 * Binds PostHog identity to the ZITADEL subject.
 *
 * Runs on boot and on every hard load — memory persistence (P1) discards the
 * distinct_id on reload, so it must be re-supplied from the session each time.
 * Unresolved session => no identify, matching the replay precondition: a
 * control honoured 300 ms late is not honoured.
 */
export function useAnalyticsIdentity(): void {
  const { user, isLoading } = useAuth()

  useEffect(() => {
    if (isLoading) {
      setAnalyticsSuppressed(true)
      return
    }
    setAnalyticsSuppressed(user?.analyticsSuppressed !== false)
    const role: UserRole = user ? (user.isAdmin ? 'operator' : 'ciso') : 'anon'
    setUserRole(role)
    if (user?.sub && user.analyticsSuppressed === false) identifyUser(user.sub)
  }, [isLoading, user])
}

/**
 * One explicit $pageview per route change (R14). capture_pageview is false, so
 * this is the only source — no history_change listener can double-count.
 *
 * The route pattern comes from the registry's sanitizeUrl, NOT from
 * sentry-route-tag's normaliseRoute: the latter passes slugs through verbatim
 * and emits ':cve', which is not a registry pattern. See the tests.
 */
export function usePageview(): void {
  const location = useLocation()
  const { user, isLoading } = useAuth()
  useEffect(() => {
    if (!isEnabled()) return
    if (isLoading || user?.analyticsSuppressed !== false) return
    const pattern = sanitizeUrl(location.pathname, PATTERNS)
    posthog.capture('$pageview', { $pathname: pattern, $current_url: pattern })
  }, [location.pathname, isLoading, user?.analyticsSuppressed])
}

/**
 * KAN-904 (codex pol-ddfc621d HIGH) — the member's in-tab replay stop.
 *
 * Turning "Session recording" OFF must stop the recorder at the click, not
 * after the save and the `/me` refresh. Until `/me` is re-read the hook below
 * still holds the old `replayOptOut: false`, so without this it kept recording
 * on `/settings` (a recordable route) during the round-trip and could restart
 * on the next in-scope navigation.
 *
 * In memory only, like the analytics chokepoint: no `localStorage`, no
 * posthog-js persisted opt-out. The objection is per account and lives on the
 * server; a reload starts from `/me`, which then carries it. The flag can only
 * DENY — it never makes `shouldRecord` true.
 *
 * Lifecycle (driven by `PrivacySection`). The stop is a set of HOLDS, one per
 * OFF save, not one shared boolean (codex pol-fix-673c930a HIGH): until
 * `/preferences` refreshes the switch still reads ON, so a second click sends a
 * second objection while the first is in flight, and one failing must not lift
 * the stop the other still needs. Recording is denied while ANY hold exists.
 *  - `stopReplayRecordingNow()` on OFF, before the save leaves: takes a new
 *    PENDING hold and returns its id;
 *  - `confirmReplayRecordingStop(id)` when that objection saved: the hold is
 *    CONFIRMED and stays, because the server now carries the objection;
 *  - `releaseReplayRecordingStop(id)` when that objection failed: releases that
 *    hold ONLY. Any other pending or confirmed hold keeps the recorder stopped;
 *    once none is left the recorder follows what the server holds;
 *  - `markReplayWithdrawal()` when an ON save leaves, and
 *    `releaseConfirmedReplayStops(mark)` once it succeeded: releases the
 *    CONFIRMED holds taken before that withdrawal (the server accepted the
 *    newer intent). A hold still pending, or taken after the withdrawal, is an
 *    objection whose outcome is unknown and is kept: deny-biased, it resolves
 *    through its own confirm/release.
 * Recording resumes only when no hold is left AND the next `/me` render says it
 * may.
 */
type ReplayStopHoldState = 'pending' | 'confirmed'
export type ReplayStopHold = number

const replayStopHolds = new Map<ReplayStopHold, ReplayStopHoldState>()
let nextReplayStopHold: ReplayStopHold = 1
const localStopListeners = new Set<() => void>()

/** True while any in-tab replay stop is held. Deny-only. */
export function isReplayStoppedLocally(): boolean {
  return replayStopHolds.size > 0
}

export function stopReplayRecordingNow(): ReplayStopHold {
  const hold = nextReplayStopHold++
  replayStopHolds.set(hold, 'pending')
  posthog.stopSessionRecording()
  // Tell the mounted hook its "already recording" ref is no longer true, so a
  // later allowed evaluation starts the recorder again instead of assuming it
  // is still running.
  for (const listener of localStopListeners) listener()
  return hold
}

export function confirmReplayRecordingStop(hold: ReplayStopHold): void {
  // Only a hold that is still held can be confirmed; a released one stays gone.
  if (replayStopHolds.has(hold)) replayStopHolds.set(hold, 'confirmed')
}

export function releaseReplayRecordingStop(hold: ReplayStopHold): void {
  replayStopHolds.delete(hold)
}

/** Watermark taken when a withdrawal (ON) save leaves. */
export function markReplayWithdrawal(): ReplayStopHold {
  return nextReplayStopHold
}

export function releaseConfirmedReplayStops(before: ReplayStopHold): void {
  for (const [hold, state] of replayStopHolds) {
    if (hold < before && state === 'confirmed') replayStopHolds.delete(hold)
  }
}

/** Test-only: one page load starts with no in-tab stop. */
export function __resetReplayStopForTests(): void {
  replayStopHolds.clear()
  nextReplayStopHold = 1
}

/**
 * KAN-32 A.10 (R10, decision O8) — session replay, re-evaluated on every route
 * change.
 *
 * O8 amended P2/P3: replay is neither full-surface nor on by default. It runs
 * only while a NAMED, TIME-BOXED investigation is open, on a subset of routes.
 * Six preconditions, all evaluated by `shouldRecord`, which DENIES by default:
 *
 *   1. VITE_POSTHOG_REPLAY_ENABLED === 'true'  (build-time, Captain's gate)
 *   2. an investigation the server says is active
 *   3. the current route is inside that investigation's list
 *   4. the session has resolved — unresolved DENIES, it never assumes
 *   5. the user is authenticated                (O2)
 *   6. the user has not objected                (O3, Art. 21)
 *
 * `{ sampling: true }` is MANDATORY. The bare `startSessionRecording()` form
 * respects the project's 0.00 sample rate and records nothing at all — the
 * wiring would look correct and produce no replays.
 *
 * MUST be mounted inside <AuthProvider> (it is, via <AnalyticsBridge/>). A hook
 * in App's body sits outside the provider, `useAuth()` returns { user: null }
 * forever, and every test still passes because they mock the auth context. That
 * defect already happened once, on A.4/A.5.
 *
 * `recording` is a ref, not state: it must not trigger a re-render, and it
 * makes start/stop idempotent so a re-render inside the same route does not
 * restart a recording already in progress.
 */
export function useReplayRecording(): void {
  const location = useLocation()
  const { user, isLoading, refreshUser } = useAuth()
  const recording = useRef(false)

  useEffect(() => {
    const onLocalStop = () => { recording.current = false }
    localStopListeners.add(onLocalStop)
    return () => { localStopListeners.delete(onLocalStop) }
  }, [])

  // KAN-594 — refresh the session just past the earliest live expiry.
  //
  // The server returns `expiresAt` = MIN across live investigations, and
  // `shouldRecord` 3b denies once that boundary passes. But `/me` is otherwise
  // fetched on mount and explicit refreshes only, so without this timer an
  // idle tab (a) kept recording an expired investigation before 3b existed,
  // and (b) keeps a still-live LATER-expiring investigation dark after the
  // earliest boundary, because the stale response can no longer be trusted and
  // 3b rightly denies all of it. The timer refetches `/me` just past the
  // boundary so the server re-serves the surviving union.
  //
  // O8 §7.0b is preserved: the client clock only SCHEDULES the refresh — the
  // recording decision still consumes exclusively what the server returned.
  // Keyed on the expiresAt STRING deliberately: if a skewed clock makes the
  // boundary look past and the refreshed response carries the same value, the
  // dep is unchanged, no new timer is armed, and there is no refetch loop.
  const investigationExpiresAt = user?.replayInvestigation?.expiresAt
  useEffect(() => {
    if (!investigationExpiresAt) return
    const boundary = Date.parse(investigationExpiresAt)
    if (!Number.isFinite(boundary)) return
    // setTimeout's delay is a SIGNED 32-BIT value: anything above 2^31−1 ms
    // (~24.85 days) fires immediately, and a 30-day window exceeds it. A
    // single CLAMPED timer is no better (Cubic round 2, PR #1183): it fires
    // days early, the refreshed response carries the same expiresAt string,
    // the effect key never changes, no new timer is armed — and the real
    // boundary is never scheduled. So the timer CHAINS: while the remaining
    // delay exceeds the ceiling, arm a max-length hop that only RE-EVALUATES
    // (hops are not refreshes); the final hop — +1s grace so the server-side
    // `now() BETWEEN` has definitively passed, floored at 1s so a past
    // boundary refetches once, promptly — is the single refreshUser call.
    // One refresh per boundary value, however many hops precede it.
    const MAX_TIMEOUT_MS = 2 ** 31 - 1
    let timer: number
    const arm = () => {
      const remaining = boundary + 1_000 - Date.now()
      if (remaining > MAX_TIMEOUT_MS) {
        timer = window.setTimeout(arm, MAX_TIMEOUT_MS)
        return
      }
      timer = window.setTimeout(() => {
        void refreshUser()
      }, Math.max(remaining, 1_000))
    }
    arm()
    // `timer` is reassigned on every hop; the cleanup closure reads the
    // variable, not a snapshot, so it clears whichever timer is live.
    return () => window.clearTimeout(timer)
  }, [investigationExpiresAt, refreshUser])

  useEffect(() => {
    // The in-tab stop (KAN-904) is checked first and only ever denies.
    const allowed = !isReplayStoppedLocally() && shouldRecord({
      deploymentEnabled: import.meta.env.VITE_POSTHOG_REPLAY_ENABLED === 'true',
      investigation: user?.replayInvestigation ?? null,
      pathname: location.pathname,
      sessionResolved: !isLoading,
      authenticated: Boolean(user?.sub),
      // `=== true` on the OBJECTION, so an absent field (older cached /me, a
      // response shape change) is NOT `false` and therefore denies. Never
      // default this to false.
      optedOut: user?.replayOptOut !== false,
    })

    if (allowed && !recording.current) {
      // Bare form respects the project's 0.00 sample rate — see above.
      posthog.startSessionRecording({ sampling: true })
      recording.current = true
      return
    }
    if (!allowed && recording.current) {
      posthog.stopSessionRecording()
      recording.current = false
    }
  }, [location.pathname, isLoading, user])
}
